import { useMemo, useState, Fragment } from "react";

import { Input, Tooltip, Switch, type FormInstance } from "antd";
import { clsx } from "clsx";

import { RVIcon, RVIconSizeOptions, RVFormCheckbox, RVRow, getColor, RVFormItem } from "@regal-voice/ui";
import { isRegalVoiceUser } from "Services/AuthService";
import { NodeTriggerType } from "Types/Journey";

import WarningBanner from "../../../../../shared/Components/WarningBanner/WarningBanner";
import { ContactConditions } from "./ContactConditions";
import { ScheduledJourney } from "./ScheduledJourneyFormItems/ScheduleJourney";
import { SegmentSelection } from "./ScheduledJourneyFormItems/SegmentSelection";
import { TriggerEventConditions } from "./TriggerEventConditions";

import styles from "./TriggerFormItems.module.scss";

function InputInteger({
    value,
    onChange,
    label,
    ...rest
}: {
    value?: any;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    label?: string;
}) {
    function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
        // value * 1 will return value if value is a number, else NaN
        const newNumber = (e.target.value as any) * 1;
        if (e.target.value === "0") {
            return;
        }
        if (Number.isNaN(newNumber)) {
            return;
        }
        if (!Number.isInteger(newNumber)) {
            return;
        }
        if (e.target.value.includes(".")) {
            return;
        }
        onChange?.(e);
    }
    return (
        <div className={clsx("flex-row align-center", styles.numberInputFormItem, styles.zeroPadding)}>
            <span>{label}</span>
            <Input value={value || ""} onChange={handleChange} className={styles.inputNumber} {...rest} />
            <Tooltip
                title="The number of times each contact can go through this
                        journey in their lifetime. Leave blank for unlimited."
                placement="topRight"
                color={getColor("purple5")}
            >
                <div className={styles.tooltip}>
                    <RVIcon size={RVIconSizeOptions.SMALL} icon="info-circle" />
                </div>
            </Tooltip>
        </div>
    );
}

export function TriggerFormItems({
    form,
    formValues = {},
}: {
    form: FormInstance;
    formValues: Record<string, any>;
}): JSX.Element {
    const [segmentUploading, setSegmentUploading] = useState<boolean>(false);
    const { triggerFiltersEvents, hiddenJourney } = formValues;

    const triggerNodeType = useMemo(() => {
        return triggerFiltersEvents?.[0]?.conditions?.[0].conditionalType;
    }, [triggerFiltersEvents]);

    const isScheduledJourney = triggerNodeType === NodeTriggerType.Scheduled;
    const canHideJourneys = isRegalVoiceUser();

    return (
        <>
            <div
                className={clsx("flex-row align-center justify-between", styles.zeroPadding)}
                style={{ paddingBottom: "8px" }}
            >
                <h4>
                    <b>
                        <TriggerTypeHeader triggerNodeType={triggerNodeType} />
                    </b>
                </h4>
            </div>

            {/* add the wrapper to be able to expand the background */}
            <div className={clsx("single-event-conditions-container", styles.singleEventConditionsContainer)}>
                <TriggerEventConditions form={form} formValues={formValues} triggerType={triggerNodeType} />
                <div className={styles.singleEventScheduledContainer}>
                    {isScheduledJourney && (
                        <SegmentSelection formValues={formValues} setSegmentUploading={setSegmentUploading} />
                    )}
                </div>
            </div>

            {isScheduledJourney && <ScheduledJourney formValues={formValues} segmentUploading={segmentUploading} />}

            <ContactConditions form={form} formValues={formValues} />

            <RVFormItem name="lifetimeLimit" label="Limit Entrances" className={styles.limitsPanel}>
                <InputInteger label="Limit lifetime entrances to" />
            </RVFormItem>

            <RVRow justifyContent="space-between" alignItems="baseline">
                <RVFormCheckbox name="disableSimultaneousEntrances">Do not allow simultaneous entrances</RVFormCheckbox>
                <Tooltip
                    title="Check to prevent contacts who are currently in-progress from entering the journey again."
                    placement="topRight"
                    color={getColor("purple5")}
                >
                    <div className={styles.tooltip}>
                        <RVIcon size={RVIconSizeOptions.SMALL} icon="info-circle" />
                    </div>
                </Tooltip>
            </RVRow>
            {canHideJourneys && (
                <>
                    <div className={clsx("flex-row align-center justify-between", styles.zeroPadding)}>
                        <h4>
                            <b>Hide Journey</b>
                        </h4>
                        <RVFormItem name="hiddenJourney" valuePropName="checked" className={styles.zeroMarginBottom}>
                            <Switch checked={hiddenJourney} />
                        </RVFormItem>
                    </div>
                </>
            )}
        </>
    );
}

function TriggerTypeHeader({ triggerNodeType }: { triggerNodeType: NodeTriggerType }) {
    const triggerTypeLabel = "Trigger Type";
    if (triggerNodeType != NodeTriggerType.Scheduled) {
        return <Fragment key={"simple-trigger-type"}>{triggerTypeLabel}</Fragment>;
    }
    return (
        <div key={"trigger-type-banner"}>
            <span>{triggerTypeLabel}</span>
            <WarningBanner>
                <>
                    Only one Scheduled Journey can be running at a time. Scheduled Journeys process 1k contacts per
                    minute. Reporting events generated by Scheduled Journeys may take additional time to update in the
                    app.
                </>
            </WarningBanner>
        </div>
    );
}
